import { HideContainer } from '@components/hide-container';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconSend } from '@components/icons/send';
import { Spinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { AttributeFieldType } from 'api/attributes/requests';
import { createTravellerJourneyActionFieldPath } from 'api/documents/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { JourneyField, TravellerJourneyActionDetailed } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { UpdateFieldsFormContent } from './helpers';

import {
  ActionProgressBar,
  EditableActionContent,
  RenderAttributeFieldLabelGroup,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  isActionError,
  useEditingContext,
} from '../common';

type ActionType = 'update_fields';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('updateFields')}
        icon={IconAttributeJourney}
        iconColor="elementPurpleInvert"
      >
        <TravellerActionActions
          action={action}
          ratioOverride={action.status === 'completed' ? 1 : 0}
          statusOverride={action.status}
        />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const status = action.status;
  const isError = isActionError(action);

  const fields = (action.data.fields ?? []) as JourneyField[];
  const updatedFields = (action.data.updatedFields ?? []) as {
    field: JourneyField;
    fieldTitle: string;
    fieldType: AttributeFieldType;
    optionValue: string;
  }[];

  const attributesQuery = useJourneyAttributes(action.journeyId);
  const attributes = attributesQuery.data ?? [];
  const userQuery = useUsers();
  const usersList = userQuery.data;

  if (attributesQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          isError={isError}
          errorMessage={
            isError
              ? {
                  title: actionErrorTitle[action.action],
                  description: action.result?.message,
                  date: action.result?.dateFailed,
                }
              : undefined
          }
          dateSuccess={action.result?.dateSucceeded}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('fields')}>
        <Stack space={4}>
          {fields.map((field, idx) => (
            <RenderAttributeFieldLabelGroup
              key={idx}
              attribute={attributes.find((attribute) => attribute.shortId === field.field)}
              field={field}
              usersList={usersList}
              savedFieldName={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.fieldTitle
              }
              savedFieldType={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.fieldType ??
                field.last_saved_type
              }
              savedOptionValue={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.optionValue
              }
              actionStatus={status}
            />
          ))}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <TravellerActionFooter
          action={action}
          startIcon={<IconSend />}
          startText={t('run')}
          retryText={t('retry')}
        />
      )}
    </Stack>
  );
};

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, { onSuccess: stopEditing });

  const filePath = createTravellerJourneyActionFieldPath(action);

  return (
    <UpdateFieldsFormContent
      action={action}
      journeyId={action.journeyId}
      isTraveller
      filePath={filePath}
      onCancel={stopEditing}
      onSubmit={(data) =>
        editMutation.mutateAsync({
          isAutomatic: true,
          action: 'update_fields',
          values: { fields: data.fields },
          description: data.description,
        })
      }
    />
  );
};

const sidebarLocale = {
  en: {
    updateFields: 'Update journey fields',
    fields: 'Fields',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    dataNotFound: 'Data not found',
    actionStatus: 'Action status',
    attributeNotFound: 'Attribute not found',
    userNotFound: 'User not found',
    optionNotFound: 'Option not found',
    run: 'Run',
    retry: 'Retry',
  },
};
